import { Typography } from "@mui/material";
import {
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2RowEmpty
} from "@qubit/autoparts";

import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { useFlag } from "~/config/flags";

import { Recipient } from "~/features/andon/Messenger";

import {
  selectAdminSummariesGrid,
  selectWorkstationStatus
} from "~/features/serviceSupport/serviceSupport.slice";
import usePrevious from "~/hooks/usePrevProps";
import { useGetUnifyPortErrorsQuery } from "~/redux/warehouse/unify.openApi";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import { WorkstationSummaryDto } from "~/types/api";

import AdminSummariesContentRow from "./AdminSummariesContentRow";

interface AdminSummariesTableProps {
  openMessageComponent: (
    event: React.MouseEvent | React.KeyboardEvent,
    newRecipients: Recipient[]
  ) => void;
}

const emptyArray: number[] = [];

const workstationAllPorts = (workstation: WorkstationSummaryDto) => {
  if (!workstation) return emptyArray;
  const ports = workstation.ports.map((p) => p.portId);
  const parentPorts = workstation.ports
    .filter((p) => p.parentPortId != null)
    .map((p) => p.parentPortId!);
  return [...ports, ...parentPorts];
};

const AdminSummariesTable = (props: AdminSummariesTableProps) => {
  const { t } = useTranslation();
  const { enableSideNavSubMenus } = useFlag();
  const pageRowLimit = 5;
  const selectedGridId = useAppSelector(selectAdminSummariesGrid);
  const selectedWorkstationStatus = useAppSelector(selectWorkstationStatus);
  const prevSelectedGridId = usePrevious(selectedGridId);
  const prevSelectedWorkstationStatus = usePrevious(selectedWorkstationStatus);
  const { data: fetchedWorkstations = [] } = useGetWorkstationsQuery();

  const [currentPage, setCurrentPage] = useState(1);

  const filteredWorkstations = useMemo(() => {
    if (!selectedGridId || !selectedWorkstationStatus.length) return [];

    return fetchedWorkstations.filter(
      (ws) =>
        ws.autostoreGridId === selectedGridId &&
        ws.status === selectedWorkstationStatus
    );
  }, [fetchedWorkstations, selectedGridId, selectedWorkstationStatus]);

  const { portErrorCountByWorkstation, isFetchingUnifyPortErrors } =
    useGetUnifyPortErrorsQuery(
      selectedGridId
        ? {
            autostoreGridId: selectedGridId,
            workstationId: null,
            limit: null
          }
        : skipToken,
      {
        selectFromResult: ({ data, isFetching }) => {
          return {
            isFetchingUnifyPortErrors: isFetching,
            portErrorCountByWorkstation: fetchedWorkstations?.reduce<{
              [key: string]: number;
            }>((acc, curr) => {
              const allPorts = workstationAllPorts(curr);
              // eslint-disable-next-line no-param-reassign
              acc[curr.id] =
                data?.filter((d) => allPorts.includes(d.item.portId)).length ??
                0;
              return acc;
            }, {})
          };
        }
      }
    );

  const sortedWorkstations = useMemo(() => {
    if (!portErrorCountByWorkstation) return filteredWorkstations;
    return [...filteredWorkstations].sort(
      (a, b) =>
        (portErrorCountByWorkstation[b.id] || 0) -
        (portErrorCountByWorkstation[a.id] || 0)
    );
  }, [filteredWorkstations, portErrorCountByWorkstation]);

  const { workstationsToDisplay, totalPageCount } = useMemo(() => {
    const workstationsToDisplay = sortedWorkstations.slice(
      (currentPage - 1) * pageRowLimit,
      pageRowLimit * currentPage
    );
    const totalPageCount =
      sortedWorkstations.length > 0
        ? Math.ceil(sortedWorkstations.length / pageRowLimit)
        : 0;
    return {
      workstationsToDisplay,
      totalPageCount
    };
  }, [currentPage, sortedWorkstations, pageRowLimit]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (currentPage <= 1) return;

    const hasSelectedGridChanged =
      !!prevSelectedGridId && selectedGridId !== prevSelectedGridId;
    const hasSelectedWorkstationStatusChanged =
      !!prevSelectedWorkstationStatus &&
      selectedWorkstationStatus !== prevSelectedWorkstationStatus;

    if (hasSelectedGridChanged || hasSelectedWorkstationStatusChanged) {
      //selected grid or workstation status has changed and the current page is greater than 1
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    //intentionally exclude `prevSelectedGridId` and `prevSelectedWorkstationStatus` from the dependency array
    //because they are derived using a `usePrevious` hook that internally uses refs
    //including them in the dependency array is unnecessary
    //and should not affect the behavior of this hook
    currentPage,
    selectedGridId,
    selectedWorkstationStatus
  ]);

  return (
    <ASTableV2
      gridTemplateColumns="repeat(9, auto)"
      gridTemplateRows="auto"
      isLoading={isFetchingUnifyPortErrors}
      pagination={{
        pageCount: totalPageCount,
        page: currentPage,
        setPage: setCurrentPage
      }}
    >
      <ASTableV2Header>
        <ASTableV2Row aria-label="active workstations header">
          <ASTableV2Cell>
            <Typography variant="tableBody">{t("device id")}</Typography>
          </ASTableV2Cell>
          <ASTableV2Cell>
            <Typography variant="tableBody">{t("user")}</Typography>
          </ASTableV2Cell>
          <ASTableV2Cell>
            <Typography variant="tableBody">{t("parent port id")}</Typography>
          </ASTableV2Cell>
          <ASTableV2Cell>
            <Typography variant="tableBody">{t("ports")}</Typography>
          </ASTableV2Cell>
          <ASTableV2Cell>
            <Typography variant="tableBody">{t("mode")}</Typography>
          </ASTableV2Cell>
          <ASTableV2Cell>
            <Typography variant="tableBody">{t("roles")}</Typography>
          </ASTableV2Cell>
          <ASTableV2Cell />
          <ASTableV2Cell>
            <Typography variant="tableBody">{t("port errors")}</Typography>
          </ASTableV2Cell>
          <ASTableV2Cell />
        </ASTableV2Row>
      </ASTableV2Header>
      {!isFetchingUnifyPortErrors && (
        <ASTableV2Body>
          {!workstationsToDisplay?.length && (
            <ASTableV2RowEmpty>{t("no workstations found")}</ASTableV2RowEmpty>
          )}
          {workstationsToDisplay.map(
            (activeWorkstation: WorkstationSummaryDto) => {
              return (
                <AdminSummariesContentRow
                  {...props}
                  activeWorkstation={activeWorkstation}
                  enableSideNavSubMenus={enableSideNavSubMenus}
                  portErrorsCount={
                    portErrorCountByWorkstation?.[activeWorkstation.id]
                  }
                  key={activeWorkstation.id}
                />
              );
            }
          )}
        </ASTableV2Body>
      )}
    </ASTableV2>
  );
};

export default AdminSummariesTable;
